import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, Text, Flex } from "@radix-ui/themes";
import PageTemplate from "@/components/page-template/PageTemplate";
import { useAuth } from "@/providers/AuthProvider";
import withPermissionRequired from "@/hoc/withPermissionRequired";
import StripeConnectPage from "@/pages/breeze/settings/StripeConnect";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

function AnythinkPayOverviewPage() {
  const { currentTenant } = useAuth();
  const navigate = useNavigate();
  const { orgId } = useParams();

  const { data: appleCreds, isLoading: appleLoading } = useQuery({
    queryKey: ["apple-iap-credentials"],
    queryFn: () => anythinkPayService.getAppleIapCredentials(),
  });

  const stripeConfigured = Boolean(currentTenant?.stripe_connect_onboarding_completed);
  const appleConfigured = Boolean(appleCreds?.is_configured);
  const anyConfigured = stripeConfigured || appleConfigured;

  // Once at least one processor is set up there's no value in keeping the cold-start visible —
  // jump straight into the unified hub (Subscriptions / Payments / Plans tabs).
  useEffect(() => {
    if (!appleLoading && anyConfigured) {
      navigate(`/org/${orgId}/integrations/anythinkpay/subscriptions`, {
        replace: true,
      });
    }
  }, [anyConfigured, appleLoading, navigate, orgId]);

  if (appleLoading || anyConfigured) return <LoadingScreen />;

  return (
    <PageTemplate
      title="AnythinkPay Integration"
      description="Connect at least one payment processor below to start taking payments. You can enable more — or change configuration — at any time from Settings → Payments."
    >
      <Flex direction="column" gap="6">
        <Card>
          <Flex direction="column" gap="3" p="4">
            <Text size="5" weight="bold">
              Connect a payment processor to get started
            </Text>
            <Text size="3" color="gray">
              AnythinkPay unifies subscriptions and one-off payments across
              Stripe (web + mobile non-digital) and Apple In-App Purchase (iOS
              digital goods). Use the tabs below to configure each — exactly
              what you'll see in Settings → Payments, just in one place to get
              you started.
            </Text>
          </Flex>
        </Card>

        {/* Same tabbed configuration UI as Settings → Payments, embedded here for the cold-start. */}
        <StripeConnectPage chrome="tab" />
      </Flex>
    </PageTemplate>
  );
}

export default withPermissionRequired(
  "anythink_payments:read",
  AnythinkPayOverviewPage,
);
